
<template lang="pug">
aside#update-request-modal
	.close
		img(:src="CloseIcon" @click="$emit('close')")
	.loading(v-if="loading") {{ loadingText || requestStore.requestStatus || "Submitting..." }}
	h3 {{ Events.EventTypeMap[workingRequest.eventType] }}{{ 'trainingType' in workingRequest ? `: ${workingRequest.trainingType}` : null }}
	.details-container

		img(:src="PersonIcon")
		.details.user-details(v-if="user")
			.name(:class="{'red': !deducedName}") {{ deducedName || "Unknown Name" }}
			.number {{ user.phone || "Unknown Number" }}
			.email {{ user.email }}

		img(:src="PetIcon")
		.details.dog-details(v-if="dog") 
			.name {{ dog.name }}
			.weight {{ dog.weight }}
			.size {{ dog.size }}
		
		template(v-if="workingRequest.eventType === Events.EventType.Training")
			img(:src="ContractIcon")
			.details.contract-details
				template(v-if="!agreements.isAgreementsReady")
					p Loading Contract...
				template(v-else-if="requestContract")
					button(@click="openDocusignEnvelope(requestContract.$id, requestContract.dsEnvelopeStatus)") View Contract
				template(v-else)
					p Unable to locate contract associated with this request

		template(v-if="workingRequest.eventType === Events.EventType.Training")
			img(:src="DiscountIcon")
			.details.contract-details
					label.label What discount is applied?
					select.info(v-model="discountPercent" placeholder="Select a discount amount")
						option(value="0") No discount
						option(value="5") 5% (Multiple dogs)
						option(value="10") 10% (Veteran)
						option(value="15") 15% (Both)

		img(:src="CalendarIcon")
		.details.trainer-details(v-if="workingRequest.eventType === Events.EventType.Training")
			.date
				input(
					v-model="inputDate"
					type="date"
					name="dateSelector"
					:min="today"
				)
			.trainer Trainer
				select(v-model="selectedTrainer" :class="{missing: selectedTrainer === null}")
					option(:value="null") Select a Trainer
					option(
						v-for="trainer in trainerStore.trainers.values()"
						:key="trainer.$id"
						:value="trainer.$id"
					) {{ trainer.nickname }}
			.length Length (weeks)
				.button-container(:class="{missing: eventLength === null}")
					button(
						v-for="i in availableWeeks"
						:key="`index-${i}`"
						:class="{selected: eventLength === i}"
						@click="eventLength = i, getPrice()"
					) {{ i }}
		.details.trainer-details(v-else-if="workingRequest.eventType === Events.EventType.Boarding")
			label Start Date/Time
			.date
				input(
					v-model="inputDate"
					type="date"
				)
				input(
					v-model="boardingStartTime"
					type="time"
				)
			.date-okay(
				v-if="workingRequest.companyPickup"
			)
				input(
					id="pickupOkay"
					v-model="boardingStartOkay"
					type="checkbox"
				)
				label(
					for="pickupOkay"
				) Company pickup okay?
			label End Date/Time
			.date
				input(
					v-model="boardingEndDate"
					type="date"
				)
				input(
					v-model="boardingEndTime"
					type="time"
				)
			.date-okay(
				v-if="workingRequest.companyDropoff"
			)
				input(
					id="dropoffOkay"
					v-model="boardingEndOkay"
					type="checkbox"
				)
				label(for="dropoffOkay") Company dropoff okay?
			div(v-if="workingRequest.endTime") ({{ ScheduleRequest.eventLengthNights(workingRequest) }} Nights)

		img(:src="PriceIcon")
		.details
			input(
					id="useEquipPrice"
					v-model="useEquipPrice"
					type="checkbox"
				)
			label(for="useEquipPrice") Add Equipment Fee ($350)

		img(:src="PriceIcon")
		.details
			//- .price(v-if="workingRequest.eventType === Events.EventType.Training") Price: {{}}
			.price(v-if="workingRequest.eventType === Events.EventType.Training && eventLength") Price: $ {{ price }}.00
			.price(v-if="workingRequest.eventType === Events.EventType.Boarding") Price: $ {{ price }}.00
			input(
					id="useCustomPrice"
					v-model="useCustomPrice"
					type="checkbox"
				)
			label(for="useCustomPrice") Use Custom Price?
			template(v-if="useCustomPrice")
				input(
					v-model="customPrice"
					type="number"
					min="1"
				)
			template(v-else)
				input(
					v-model="estimatedPrice"
					disabled
					type="input"
				)
	ul.errors
		li.error-info(v-if="!deducedName") User does not have a name. Please have them set their name in the customer portal.
		li.error-info(v-if="error") {{ error }}
	.buttons
		button(
			:disabled="loading"
			@click="declineRequest"
		) Decline
		button(
			:disabled="!isValid || loading"
			@click="acceptRequest"
		) Submit
</template>
<!-- eslint-disable @typescript-eslint/no-non-null-assertion -->
 <!-- eslint-disable max-lines -->
<script setup lang="ts">
import { Events, Invoices, ScheduleRequest } from "@busy-human/k9-library";
import type { DataInstance } from "@busy-human/gearbox";
import { ref, watch, computed, onMounted, onUpdated } from "vue";
import { useScheduleRequests } from "@/store/useScheduleRequests";
import { useUserspace } from "@/store/useUsers";
import { useDogspace } from "@/store/dogspaces";
import { useCalendars } from "@/store/useCalendars";
import {MGMT_CONSTS} from "@/util/constants";
import { useTrainers } from "@/store/useTrainers";
import { useToast } from "vue-toastification";
import { storage } from '@/util/firebase';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';

/* SVGs */
import DiscountIcon from "@/resources/icons/discount.svg";
import CloseIcon from "@/resources/icons/close.svg";
import PersonIcon from "@/resources/icons/person.svg";
import ContractIcon from "@/resources/icons/contract.svg";
import CalendarIcon from "@/resources/icons/calendar_month.svg";
import PetIcon from "@/resources/icons/pets.svg";
import PriceIcon from "@/resources/icons/price.svg";
import { useAgreements } from "@/store/useAgreements";
import { getAgreementUrl } from "@/util/functions";
import { Agreement } from "@busy-human/contract-module";

const toast = useToast();

const msPerMinute = 60000;

type Props = {
	id: string
}
const props = defineProps<Props>();
type Emits = {
	(e: "close"): void
}
const emit = defineEmits<Emits>();
const requestStore = useScheduleRequests();
const userStore = useUserspace();
const agreements = useAgreements();
const dogStore = useDogspace();
const calendarStore = useCalendars();
const trainerStore = useTrainers();

const today             = ref(new Date().toISOString().split('T')[0]);
const workingRequest    = ref<DataInstance<ScheduleRequest.Model>>({... requestStore.requests.get(props.id)!});
const requestContract   = ref<DataInstance<Agreement.Model<any, any>> | null>(agreements.getAgreementFromRequestId(props.id) || null);
const inputDate         = ref<string>("");
const boardingStartTime = ref<string>("");
const boardingEndDate   = ref<string>("");
const boardingEndTime   = ref<string>("");
const boardingStartOkay = ref<boolean>(false);
const boardingEndOkay   = ref<boolean>(false);
const discountPercent   = ref<number>(0);
const selectedTrainer   = ref<string | null>(null);
const eventLength       = ref<number | null>(null);
const loadingText		= ref<string | null>(null);

// const boardingPrice = Invoices.calculatePriceOfBoarding(workingRequest.value)
// const trainingPrice  = Invoices.EventPricing[req.trainingType]?.[weeks];
const price = ref<number | null>(null);

watch(() => props.id, id => {
	workingRequest.value = {... requestStore.requests.get(id)!};

	// Generate a timestamp with a local timezone offset
	let d = new Date(workingRequest.value.startTime);
	d = new Date(d.getTime() - (d.getTimezoneOffset() * msPerMinute));
	let ts = d.toISOString().slice(0, -1);
	let tmp = ts.indexOf('T');
	inputDate.value = ts.substring(0, tmp);

	if(workingRequest.value.eventType === Events.EventType.Boarding) {
		boardingStartTime.value = ts.substring(tmp + 1);

		d = new Date(workingRequest.value.endTime);
		d = new Date(d.getTime() - (d.getTimezoneOffset() * msPerMinute));
		ts = d.toISOString().slice(0, -1);

		tmp = ts.indexOf('T');
		boardingEndDate.value = ts.substring(0, tmp);
		boardingEndTime.value = ts.substring(tmp + 1);
	}else if(id){
		agreements.waitForReady().then(() => {
			requestContract.value = agreements.getAgreementFromRequestId(props.id) || null;
		});
		
	}
}, {immediate: true});

watch(inputDate, d => {
	if(workingRequest.value.eventType === Events.EventType.Boarding) {
		const tmp = new Date(`${d}T${boardingStartTime.value}`);
		workingRequest.value.startTime = tmp.toISOString();
	} else {
		const tmp = new Date(d);
		workingRequest.value.startTime = new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate()+1).toISOString();
	}
});

watch(boardingEndDate, d => {
	if(workingRequest.value.eventType === Events.EventType.Boarding) {
		const tmp = new Date(`${d}T${boardingEndTime.value}`);
		workingRequest.value.endTime = new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate(), tmp.getHours(), tmp.getMinutes(), tmp.getSeconds()).toISOString();
	}
});

watch(boardingStartTime, t => {
	if(workingRequest.value.eventType === Events.EventType.Boarding) {
		const tmp = new Date(`${inputDate.value}T${t}`);
		workingRequest.value.startTime = tmp.toISOString();
	}
});

watch(boardingEndTime, t => {
	if(workingRequest.value.eventType === Events.EventType.Boarding) {
		const tmp = new Date(`${boardingEndDate.value}T${t}`);
		workingRequest.value.endTime = tmp.toISOString();
	}
});

const user = computed(() => userStore.users[workingRequest.value.userId]);
const dog = computed(() => dogStore.dogs[workingRequest.value.dogId]);

const deducedName = computed(() => (user.value?.name?.fullName)?.trim());

const isValid = computed(() => {
	if(!deducedName.value) return false;
	if(workingRequest.value.eventType === Events.EventType.Training) {
		return !!eventLength.value && !!selectedTrainer.value;
	} else if(workingRequest.value.eventType === Events.EventType.Boarding) {
		return (!workingRequest.value.companyPickup || boardingStartOkay.value) &&
			(!workingRequest.value.companyDropoff || boardingEndOkay.value);
	} else {
		return true;
	}
});

const EQUIP_PRICE = 350;
const useEquipPrice = ref(false);

const getPrice = () => {
	if(workingRequest.value.eventType === Events.EventType.Training) {
		const tmpPrice = Invoices.EventPricing[workingRequest.value.trainingType]?.[eventLength.value ?? 0] ?? null;
		if((tmpPrice || tmpPrice === 0) && useEquipPrice.value ){
			price.value = tmpPrice + EQUIP_PRICE;
		}else{
			price.value = tmpPrice;
		}
	} else if(workingRequest.value.eventType === Events.EventType.Boarding) {
		price.value = Invoices.calculatePriceOfBoarding(workingRequest.value);
	}
};

onUpdated(() => {
	getPrice();
});

onMounted(() => {
	getPrice();
	console.log("REQUEST INFO:", workingRequest.value);
	// document.getElementsByName("dateSelector")[0].setAttribute('min', today);
});

const availableWeeks = computed<number[]>(() => {
	if(workingRequest.value.eventType !== Events.EventType.Training){
		return [];
	} else {
		return MGMT_CONSTS.EVENT_LENGTH[workingRequest.value.trainingType];
	}
});

const loading = ref(false);
const error = ref<null | string>(null);
async function declineRequest() {
	loading.value = true;
	await requestStore.declineRequest(props.id);
	loading.value = false;
	emit('close');
}
const useCustomPrice = ref(false);
const customPrice = ref(1);
const estimatedPrice = computed(() => {
	if(!eventLength.value) return 'Enter Price';
	if(workingRequest.value.eventType === Events.EventType.Training){
		return Invoices.EventPricing[workingRequest.value.trainingType]?.[eventLength.value];
	}else if(workingRequest.value.eventType === Events.EventType.Boarding){
		return Invoices.calculatePriceOfBoarding(workingRequest.value) || 'N/A';
	}else{
		return 'N/A';
	}
});

onMounted(() => {
	console.log("REQUEST INFO: ", workingRequest.value);
});

// eslint-disable-next-line complexity
async function acceptRequest() {
	if(!dog.value) return;
	loading.value = true;

	console.log("Accepting...");
	error.value = null;

	try {
		// TODO: This could be optimized a little bit
		if(workingRequest.value.eventType === Events.EventType.Training) {
			if(!eventLength.value) throw new Error("Event length not set");
			if(useCustomPrice.value || (useEquipPrice.value && price.value !== null)){
				await requestStore.acceptRequest({
					reqId: props.id, 
					req: workingRequest.value, 
					dog: dog.value, 
					trainerId: selectedTrainer.value || undefined, 
					discountPercent: discountPercent.value,
					weeks: eventLength.value, 
					customPrice: useCustomPrice.value ? customPrice.value : price.value!,
					owner: user.value
				});
			}else{
				await requestStore.acceptRequest({
					reqId: props.id, 
					req: workingRequest.value, 
					dog: dog.value, 
					trainerId: selectedTrainer.value || undefined, 
					discountPercent: discountPercent.value,
					weeks: eventLength.value,
					owner: user.value
				});
			}
		} else if(workingRequest.value.eventType === Events.EventType.Boarding) {
			if(useCustomPrice.value || (useEquipPrice.value && price.value !== null)){
				await requestStore.acceptRequest({
					reqId: props.id, 
					req: workingRequest.value, 
					dog: dog.value, 
					discountPercent: discountPercent.value,
					customPrice: useCustomPrice.value ? customPrice.value : price.value!,
					owner: user.value
				});
			}else{
				await requestStore.acceptRequest({
					reqId: props.id, 
					req: workingRequest.value, 
					discountPercent: discountPercent.value,
					dog: dog.value, 
					owner: user.value
				});
			}
		}
		toast(`Successfully accepted request for ${dog.value.name} on ${new Date(workingRequest.value.startTime).toLocaleDateString()}`);
		console.log("Done!...");
		console.log("ACCEPTED:", workingRequest.value);
		emit('close');
	} catch (e) {
		console.error(e);
		toast.error(`Error while accepting request for ${dog.value.name}.`);
		error.value = `Error while accepting: ${(e as Error).message}`;
	} finally {
		loading.value = false;
	}
}

const openDocusignEnvelope = async (agreementId: string, status: string) => {
	if (status !== "completed") {
		error.value = "Cannot open incomplete contract";
		return;
	}
	loading.value = false;
	// console.log("HERE:", agreementId)
	error.value = null;
	const agreement = agreements.requests.get(agreementId);
	if (agreement) {
		console.log("Agreement", agreement);
		
		try {
			let agreementUrl = '';
			if(agreement?.pdfUrl){
				const fileRef = storageRef(storage, agreement?.pdfUrl);

				agreementUrl = await getDownloadURL(fileRef);
			}else{
				const data = await getAgreementUrl({ agreementId: agreement.$id, isSender: true });

				agreementUrl = data.data.url;
			}

			if(!agreementUrl) throw new Error('Unable to access contract');
			loading.value = false;
			window.open(agreementUrl, '_blank');
			
		} catch (e) {
			error.value = "Unable to access Contract";
			loading.value = false;
		}

	}
	// console.log("HERE 2:", loading.value)
	// loading.value = false;
};
</script>

<!-- eslint-disable max-lines -->
<style lang="scss">
aside#update-request-modal {
    position: absolute;
    top: 0;
    z-index: 10;
	max-height: calc(100vh - 8rem);
    margin-left: .5rem;
    overflow: auto;
    cursor: default;
    background: $color-white;
    border: 1px solid $color-black;
    border-radius: .5rem;
    box-shadow: 2px 2px 0 $color-black;

    .loading {
        position: absolute;
        top: 0;
        left: 0;
        display: grid;
        place-items: center;
        width: 100%;
        height: 100%;
        background: rgba($color-white, 0.75);
        backdrop-filter: blur(4px);
    }

    .missing {
        outline: 2px dashed $color-red;
    }

    .errors {
        padding: 0 1rem;
        color: $color-red;
    }

    .details-container {
        display: grid;
        grid-template-columns: 1fr 2fr;

        img {
            width: 2rem;
            padding-top: 1rem;
            padding-left: 1rem;
        }

        .date {
            white-space: nowrap;
        }

        .date-okay:hover * {
            cursor: pointer;
        }
    }

    .close img {
        border-radius: 50%;

        &:hover {
            cursor: pointer;
            outline: 2px solid $color-black;
        }

        &:active {
            background: $color-white;
            transform: translate(-1px, -1px);
        }
    }

    h3 {
        margin: unset;
        margin-left: 4rem;
        font-weight: normal;
        text-transform: capitalize;
        white-space: nowrap;
    }

    .details {
        padding: .5rem;
        margin: 1rem;
        border: 1px solid $color-gray-62;
        border-radius: .5rem;

        .red {
            color: red;
        }
    }

    .length {
        display: flex;
        align-items: center;
        white-space: nowrap;

        .button-container {
            display: inline-flex;
            gap: 1px;
            overflow: hidden;
            background: $color-black;
            border: 1px solid $color-black;
            border-radius: .5rem;
        }

        button {
            padding: .5rem;
            margin: unset;
            color: $color-gray-62;
            background: $color-white;
            border-radius: unset;

        }

        button:hover {
            color: $color-white;
            background: $color-gray-77;
        }

        button.selected {
            color: $color-black;
            background: $color-white;
        }
    }

    .buttons {
        display: flex;
        gap: 1rem;
        justify-content: flex-end;
        padding: 1rem;
    }
}
</style>